import {
  Sun,
  Inbox,
  Target,
  FolderKanban,
  Building2,
  CheckSquare,
  LogOut,
  Settings,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { crm } from '@/lib/crm-client';
import { cn } from '@/lib/utils';
import { GlobalSearch } from '@/components/GlobalSearch';

/**
 * Warnt, wenn das Datenverzeichnis den naechsten Deploy nicht ueberleben
 * wuerde. Bewusst dauerhaft und nicht wegklickbar: Ein stiller Datenverlust
 * ist genau das, was hier schon einmal passiert ist.
 */
function SpeicherWarnung() {
  const [warnung, setWarnung] = useState<string | null>(null);

  useEffect(() => {
    void crm.system
      .storage()
      .then((s) => setWarnung(s.warning))
      .catch(() => setWarnung(null));
  }, []);

  if (!warnung) return null;

  return (
    <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
      <strong>Achtung:</strong> {warnung}
    </div>
  );
}

export type Route =
  | 'heute'
  | 'posteingang'
  | 'akquise'
  | 'projekte'
  | 'firmen'
  | 'aufgaben'
  | 'einstellungen';

type NavEintrag = {
  key: Route;
  label: string;
  icon: LucideIcon;
};

/**
 * Sechs Eintraege, benannt nach dem, was man gerade tut — nicht nach der
 * Tabelle, in der es liegt. Zwanzig Eintraege nach Tabellen verlangten, dass
 * man erst wusste, wo etwas wohnt, bevor man es fand.
 *
 * Die Einstellungen stehen nicht hier, sondern unten in der Fusszeile: dort
 * richtet man etwas ein, man arbeitet nicht darin.
 */
const NAV: NavEintrag[] = [
  { key: 'heute', label: 'Heute', icon: Sun },
  { key: 'posteingang', label: 'Posteingang', icon: Inbox },
  { key: 'akquise', label: 'Akquise', icon: Target },
  { key: 'projekte', label: 'Projekte', icon: FolderKanban },
  { key: 'firmen', label: 'Firmen', icon: Building2 },
  { key: 'aufgaben', label: 'Aufgaben', icon: CheckSquare },
];

const NAV_BUTTON_KLASSE =
  'app-no-drag flex shrink-0 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';

function NavButton({
  active,
  label,
  icon: Icon,
  onClick,
}: {
  active: boolean;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        NAV_BUTTON_KLASSE,
        active
          ? 'bg-sidebar-active text-white shadow-sm'
          : 'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-foreground',
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

interface Props {
  route: Route;
  onNavigate: (r: Route) => void;
  children: React.ReactNode;
  topbar?: React.ReactNode;
  /** Angemeldetes Konto — in der Web-Variante immer gesetzt. */
  userLabel?: string;
  onLogout?: () => void;
}

export function AppShell({
  route,
  onNavigate,
  children,
  topbar,
  userLabel,
  onLogout,
}: Props) {
  return (
    <div className="flex h-full">
      {/* Sidebar */}
      <aside className="flex w-60 shrink-0 flex-col bg-sidebar text-sidebar-foreground">
        <div className="app-drag flex h-14 shrink-0 items-center gap-2 px-5 pt-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand text-sm font-bold text-brand-foreground">
            e
          </div>
          <span className="text-sm font-semibold tracking-tight">epasu CRM</span>
        </div>

        <nav className="mt-4 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto scrollbar-thin px-3 pb-2">
          {NAV.map(({ key, label, icon }) => (
            <NavButton
              key={key}
              active={route === key}
              label={label}
              icon={icon}
              onClick={() => onNavigate(key)}
            />
          ))}
        </nav>

        <div className="shrink-0 border-t border-sidebar-border p-3">
          <button
            onClick={() => onNavigate('einstellungen')}
            className={cn(
              'app-no-drag flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              route === 'einstellungen'
                ? 'bg-sidebar-active text-white shadow-sm'
                : 'text-sidebar-muted hover:bg-white/5 hover:text-sidebar-foreground',
            )}
          >
            <Settings className="h-4 w-4" />
            Einstellungen
          </button>

          {userLabel && (
            <div className="mt-2 flex items-center justify-between gap-2 px-3">
              <span className="truncate text-[11px] text-sidebar-muted" title={userLabel}>
                {userLabel}
              </span>
              {onLogout && (
                <button
                  onClick={onLogout}
                  title="Abmelden"
                  className="app-no-drag shrink-0 rounded p-1 text-sidebar-muted transition-colors hover:bg-white/5 hover:text-sidebar-foreground"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="app-drag flex h-14 items-center justify-between border-b border-border bg-card px-6">
          <div className="flex items-center gap-4">
            <GlobalSearch />
            {topbar}
          </div>
        </header>
        <SpeicherWarnung />
        <main className="flex-1 overflow-auto scrollbar-thin p-6">{children}</main>
      </div>
    </div>
  );
}
